/**
 * Resource Service
 * Handles CRUD operations for ResourceType, ResourceCatalog, and ClientResourceMap models
 */

import { API, graphqlOperation } from 'aws-amplify';
import {
  listResourceTypes,
  getResourceType,
  listResourceCatalogs,
  getResourceCatalog,
  resourceCatalogByResourceCode,
  resourceCatalogByType,
  resourceCatalogByStatus,
  listClientResourceMaps,
  getClientResourceMap,
  clientResourceMapByClient,
  clientResourceMapByResource,
} from 'graphql/queries';
import {
  createResourceType as createResourceTypeMutation,
  updateResourceType as updateResourceTypeMutation,
  deleteResourceType as deleteResourceTypeMutation,
  createResourceCatalog as createResourceCatalogMutation,
  updateResourceCatalog as updateResourceCatalogMutation,
  deleteResourceCatalog as deleteResourceCatalogMutation,
  createClientResourceMap as createClientResourceMapMutation,
  updateClientResourceMap as updateClientResourceMapMutation,
  deleteClientResourceMap as deleteClientResourceMapMutation,
} from 'graphql/mutations';
import * as logger from 'utils/logger';

// Pagination configuration
const DEFAULT_PAGE_SIZE = 50;
const MAX_PAGE_SIZE = 100;

// =============================================================================
// RESOURCE TYPE OPERATIONS
// =============================================================================

/**
 * Fetches all resource types
 * @param {Object} filter - Optional filter criteria
 * @returns {Promise<Array>} Array of resource type records
 * @throws {Error} If the GraphQL query fails
 */
export const fetchResourceTypes = async (filter = null) => {
  try {
    const params = {
      limit: MAX_PAGE_SIZE,
    };

    if (filter) {
      params.filter = filter;
    }

    const result = await API.graphql(graphqlOperation(listResourceTypes, params));

    return result.data.listResourceTypes.items || [];
  } catch (error) {
    logger.error('Failed to fetch resource types', { error });
    throw new Error('Unable to fetch resource types. Please try again.');
  }
};

/**
 * Fetches a single resource type by ID
 * @param {string} id - Resource type ID
 * @returns {Promise<Object>} Resource type record
 * @throws {Error} If the GraphQL query fails
 */
export const fetchResourceType = async (id) => {
  if (!id || typeof id !== 'string') {
    throw new Error('Valid id is required');
  }

  try {
    const result = await API.graphql(graphqlOperation(getResourceType, { id }));
    return result.data.getResourceType;
  } catch (error) {
    logger.error('Failed to fetch resource type', { id, error });
    throw new Error('Unable to fetch resource type. Please try again.');
  }
};

/**
 * Creates a new resource type
 * @param {Object} input - Resource type data
 * @param {string} input.name - Resource type name (required)
 * @param {string} input.resourcePrefix - Single letter prefix for resource codes (required)
 * @param {string} input.status - Resource status (ACTIVE/INACTIVE) (required)
 * @param {string} input.icon - Icon name (optional)
 * @param {string} input.description - Description (optional)
 * @param {Array<string>} input.domainRestrictions - Array of allowed domains (optional)
 * @param {Object} input.metadata - Additional metadata (optional)
 * @returns {Promise<Object>} Created resource type record
 * @throws {Error} If validation fails or GraphQL mutation fails
 */
export const createResourceType = async (input) => {
  // Validate required fields
  if (!input.name || typeof input.name !== 'string') {
    throw new Error('Resource type name is required');
  }
  if (!input.resourcePrefix || typeof input.resourcePrefix !== 'string') {
    throw new Error('Resource prefix is required');
  }
  if (!/^[A-Z]$/.test(input.resourcePrefix)) {
    throw new Error('Resource prefix must be a single uppercase letter (A-Z)');
  }
  if (!input.status || !['ACTIVE', 'INACTIVE'].includes(input.status)) {
    throw new Error('Valid status (ACTIVE/INACTIVE) is required');
  }

  try {
    const result = await API.graphql(
      graphqlOperation(createResourceTypeMutation, { input })
    );
    logger.info('Resource type created successfully', { id: result.data.createResourceType.id });
    return result.data.createResourceType;
  } catch (error) {
    logger.error('Failed to create resource type', { input, error });
    throw new Error('Unable to create resource type. Please try again.');
  }
};

/**
 * Updates an existing resource type
 * @param {Object} input - Resource type update data
 * @param {string} input.id - Resource type ID (required)
 * @param {string} input.resourcePrefix - Single letter prefix for resource codes (optional for update)
 * @returns {Promise<Object>} Updated resource type record
 * @throws {Error} If validation fails or GraphQL mutation fails
 */
export const updateResourceType = async (input) => {
  if (!input.id || typeof input.id !== 'string') {
    throw new Error('Resource type ID is required');
  }

  // Validate resourcePrefix if provided
  if (input.resourcePrefix && !/^[A-Z]$/.test(input.resourcePrefix)) {
    throw new Error('Resource prefix must be a single uppercase letter (A-Z)');
  }

  // Validate status if provided
  if (input.status && !['ACTIVE', 'INACTIVE'].includes(input.status)) {
    throw new Error('Valid status (ACTIVE/INACTIVE) is required');
  }

  try {
    const result = await API.graphql(
      graphqlOperation(updateResourceTypeMutation, { input })
    );
    logger.info('Resource type updated successfully', { id: input.id });
    return result.data.updateResourceType;
  } catch (error) {
    logger.error('Failed to update resource type', { input, error });
    throw new Error('Unable to update resource type. Please try again.');
  }
};

/**
 * Deletes a resource type
 * @param {string} id - Resource type ID
 * @returns {Promise<Object>} Deleted resource type record
 * @throws {Error} If validation fails or GraphQL mutation fails
 */
export const deleteResourceType = async (id) => {
  if (!id || typeof id !== 'string') {
    throw new Error('Valid id is required');
  }

  try {
    const result = await API.graphql(
      graphqlOperation(deleteResourceTypeMutation, { input: { id } })
    );
    logger.info('Resource type deleted successfully', { id });
    return result.data.deleteResourceType;
  } catch (error) {
    logger.error('Failed to delete resource type', { id, error });
    throw new Error('Unable to delete resource type. Please try again.');
  }
};

// =============================================================================
// RESOURCE CATALOG OPERATIONS
// =============================================================================

/**
 * Fetches all resource catalog entries
 * @param {Object} filter - Optional filter criteria
 * @returns {Promise<Array>} Array of resource catalog records
 * @throws {Error} If the GraphQL query fails
 */
export const fetchResourceCatalogs = async (filter = null) => {
  try {
    const params = {
      limit: MAX_PAGE_SIZE,
    };

    if (filter) {
      params.filter = filter;
    }

    const result = await API.graphql(graphqlOperation(listResourceCatalogs, params));

    return result.data.listResourceCatalogs.items || [];
  } catch (error) {
    logger.error('Failed to fetch resource catalogs', { error });
    throw new Error('Unable to fetch resource catalogs. Please try again.');
  }
};

/**
 * Fetches resource catalog entries by resource type
 * @param {string} resourceTypeId - Resource type ID
 * @param {string} status - Optional status filter (ACTIVE/INACTIVE)
 * @returns {Promise<Array>} Array of resource catalog records
 * @throws {Error} If the GraphQL query fails
 */
export const fetchResourceCatalogsByType = async (resourceTypeId, status = null) => {
  if (!resourceTypeId || typeof resourceTypeId !== 'string') {
    throw new Error('Valid resourceTypeId is required');
  }

  try {
    const params = {
      resourceTypeId,
      limit: MAX_PAGE_SIZE,
    };

    if (status) {
      params.status = { eq: status };
    }

    const result = await API.graphql(graphqlOperation(resourceCatalogByType, params));

    return result.data.resourceCatalogByType.items || [];
  } catch (error) {
    logger.error('Failed to fetch resource catalogs by type', { resourceTypeId, error });
    throw new Error('Unable to fetch resource catalogs. Please try again.');
  }
};

/**
 * Fetches resource catalog entries by status
 * @param {string} status - Status filter (ACTIVE/INACTIVE)
 * @returns {Promise<Array>} Array of resource catalog records
 * @throws {Error} If the GraphQL query fails
 */
export const fetchResourceCatalogsByStatus = async (status) => {
  if (!status || !['ACTIVE', 'INACTIVE'].includes(status)) {
    throw new Error('Valid status (ACTIVE/INACTIVE) is required');
  }

  try {
    const params = {
      status,
      limit: MAX_PAGE_SIZE,
    };

    const result = await API.graphql(graphqlOperation(resourceCatalogByStatus, params));

    return result.data.resourceCatalogByStatus.items || [];
  } catch (error) {
    logger.error('Failed to fetch resource catalogs by status', { status, error });
    throw new Error('Unable to fetch resource catalogs. Please try again.');
  }
};

/**
 * Fetches a resource catalog entry by resource code
 * @param {string} resourceCode - Resource code (e.g., "R0002")
 * @returns {Promise<Object|null>} Resource catalog record or null if not found
 * @throws {Error} If the GraphQL query fails
 */
export const fetchResourceCatalogByCode = async (resourceCode) => {
  if (!resourceCode || typeof resourceCode !== 'string') {
    throw new Error('Valid resourceCode is required');
  }

  try {
    const result = await API.graphql(
      graphqlOperation(resourceCatalogByResourceCode, { resourceCode })
    );

    const items = result.data.resourceCatalogByResourceCode.items || [];
    return items.length > 0 ? items[0] : null;
  } catch (error) {
    logger.error('Failed to fetch resource catalog by code', { resourceCode, error });
    throw new Error('Unable to fetch resource catalog. Please try again.');
  }
};

/**
 * Fetches a single resource catalog entry by ID
 * @param {string} id - Resource catalog ID
 * @returns {Promise<Object>} Resource catalog record
 * @throws {Error} If the GraphQL query fails
 */
export const fetchResourceCatalog = async (id) => {
  if (!id || typeof id !== 'string') {
    throw new Error('Valid id is required');
  }

  try {
    const result = await API.graphql(graphqlOperation(getResourceCatalog, { id }));
    return result.data.getResourceCatalog;
  } catch (error) {
    logger.error('Failed to fetch resource catalog', { id, error });
    throw new Error('Unable to fetch resource catalog. Please try again.');
  }
};

/**
 * Creates a new resource catalog entry
 * @param {Object} input - Resource catalog data
 * @param {string} input.resourceCode - Unique resource code (required)
 * @param {string} input.resourceTypeId - Resource type ID (required)
 * @param {string} input.domain - Domain (required)
 * @param {string} input.name - Resource name (required)
 * @param {Array<string>} input.defaultActions - Default actions (required)
 * @param {string} input.status - Resource status (ACTIVE/INACTIVE) (required)
 * @param {number} input.version - Version number (required)
 * @param {string} input.description - Description (optional)
 * @param {string} input.icon - Icon name (optional)
 * @param {Object} input.metadata - Additional metadata (optional)
 * @returns {Promise<Object>} Created resource catalog record
 * @throws {Error} If validation fails or GraphQL mutation fails
 */
export const createResourceCatalog = async (input) => {
  // Validate required fields
  if (!input.resourceCode || typeof input.resourceCode !== 'string') {
    throw new Error('Resource code is required');
  }
  if (!input.resourceTypeId || typeof input.resourceTypeId !== 'string') {
    throw new Error('Resource type ID is required');
  }
  if (!input.domain || typeof input.domain !== 'string') {
    throw new Error('Domain is required');
  }
  if (!input.name || typeof input.name !== 'string') {
    throw new Error('Resource name is required');
  }
  if (!input.defaultActions || !Array.isArray(input.defaultActions) || input.defaultActions.length === 0) {
    throw new Error('At least one default action is required');
  }
  if (!input.status || !['ACTIVE', 'INACTIVE'].includes(input.status)) {
    throw new Error('Valid status (ACTIVE/INACTIVE) is required');
  }
  if (typeof input.version !== 'number' || input.version < 1) {
    throw new Error('Valid version number is required');
  }

  // Validate resource code format and uniqueness
  const validation = await validateResourceCode(input.resourceCode, input.resourceTypeId);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  try {
    const result = await API.graphql(
      graphqlOperation(createResourceCatalogMutation, { input })
    );
    logger.info('Resource catalog created successfully', { id: result.data.createResourceCatalog.id });
    return result.data.createResourceCatalog;
  } catch (error) {
    logger.error('Failed to create resource catalog', { input, error });
    throw new Error('Unable to create resource catalog. Please try again.');
  }
};

/**
 * Updates an existing resource catalog entry
 * @param {Object} input - Resource catalog update data
 * @param {string} input.id - Resource catalog ID (required)
 * @returns {Promise<Object>} Updated resource catalog record
 * @throws {Error} If validation fails or GraphQL mutation fails
 */
export const updateResourceCatalog = async (input) => {
  if (!input.id || typeof input.id !== 'string') {
    throw new Error('Resource catalog ID is required');
  }

  // Validate status if provided
  if (input.status && !['ACTIVE', 'INACTIVE'].includes(input.status)) {
    throw new Error('Valid status (ACTIVE/INACTIVE) is required');
  }

  // Validate defaultActions if provided
  if (input.defaultActions && (!Array.isArray(input.defaultActions) || input.defaultActions.length === 0)) {
    throw new Error('Default actions must be a non-empty array');
  }

  try {
    const result = await API.graphql(
      graphqlOperation(updateResourceCatalogMutation, { input })
    );
    logger.info('Resource catalog updated successfully', { id: input.id });
    return result.data.updateResourceCatalog;
  } catch (error) {
    logger.error('Failed to update resource catalog', { input, error });
    throw new Error('Unable to update resource catalog. Please try again.');
  }
};

/**
 * Deletes a resource catalog entry
 * @param {string} id - Resource catalog ID
 * @returns {Promise<Object>} Deleted resource catalog record
 * @throws {Error} If validation fails or GraphQL mutation fails
 */
export const deleteResourceCatalog = async (id) => {
  if (!id || typeof id !== 'string') {
    throw new Error('Valid id is required');
  }

  try {
    const result = await API.graphql(
      graphqlOperation(deleteResourceCatalogMutation, { input: { id } })
    );
    logger.info('Resource catalog deleted successfully', { id });
    return result.data.deleteResourceCatalog;
  } catch (error) {
    logger.error('Failed to delete resource catalog', { id, error });
    throw new Error('Unable to delete resource catalog. Please try again.');
  }
};

// =============================================================================
// CLIENT RESOURCE MAP OPERATIONS
// =============================================================================

/**
 * Fetches all client resource maps for a specific client
 * @param {string} clientId - Client ID
 * @param {Object} filter - Optional filter criteria
 * @returns {Promise<Array>} Array of client resource map records sorted by order
 * @throws {Error} If the GraphQL query fails
 */
export const fetchClientResourceMaps = async (clientId, filter = null) => {
  if (!clientId || typeof clientId !== 'string') {
    throw new Error('Valid clientId is required');
  }

  try {
    const params = {
      clientId,
      sortDirection: 'ASC',
      limit: MAX_PAGE_SIZE,
    };

    if (filter) {
      params.filter = filter;
    }

    const result = await API.graphql(
      graphqlOperation(clientResourceMapByClient, params)
    );

    return result.data.clientResourceMapByClient.items || [];
  } catch (error) {
    logger.error('Failed to fetch client resource maps', { clientId, error });
    throw new Error('Unable to fetch client resource maps. Please try again.');
  }
};

/**
 * Fetches enabled client resource maps for a specific client
 * @param {string} clientId - Client ID
 * @returns {Promise<Array>} Array of enabled client resource map records sorted by order
 * @throws {Error} If the GraphQL query fails
 */
export const fetchEnabledClientResourceMaps = async (clientId) => {
  return fetchClientResourceMaps(clientId, { isEnabled: { eq: true } });
};

/**
 * Fetches client resource maps by resource ID
 * @param {string} resourceId - Resource ID
 * @returns {Promise<Array>} Array of client resource map records
 * @throws {Error} If the GraphQL query fails
 */
export const fetchClientResourceMapsByResource = async (resourceId) => {
  if (!resourceId || typeof resourceId !== 'string') {
    throw new Error('Valid resourceId is required');
  }

  try {
    const params = {
      resourceId,
      limit: MAX_PAGE_SIZE,
    };

    const result = await API.graphql(
      graphqlOperation(clientResourceMapByResource, params)
    );

    return result.data.clientResourceMapByResource.items || [];
  } catch (error) {
    logger.error('Failed to fetch client resource maps by resource', { resourceId, error });
    throw new Error('Unable to fetch client resource maps. Please try again.');
  }
};

/**
 * Fetches a single client resource map by ID
 * @param {string} id - Client resource map ID
 * @returns {Promise<Object>} Client resource map record
 * @throws {Error} If the GraphQL query fails
 */
export const fetchClientResourceMap = async (id) => {
  if (!id || typeof id !== 'string') {
    throw new Error('Valid id is required');
  }

  try {
    const result = await API.graphql(graphqlOperation(getClientResourceMap, { id }));
    return result.data.getClientResourceMap;
  } catch (error) {
    logger.error('Failed to fetch client resource map', { id, error });
    throw new Error('Unable to fetch client resource map. Please try again.');
  }
};

/**
 * Creates a new client resource map
 * @param {Object} input - Client resource map data
 * @param {string} input.clientId - Client ID (required)
 * @param {string} input.resourceId - Resource ID (required)
 * @param {boolean} input.isEnabled - Whether resource is enabled (required)
 * @param {number} input.order - Display order (required)
 * @param {string} input.customLabel - Custom label (optional)
 * @param {string} input.customTooltip - Custom tooltip (optional)
 * @param {string} input.customDescription - Custom description (optional)
 * @param {string} input.customIcon - Custom icon (optional)
 * @param {Array<string>} input.allowedActionsOverride - Allowed actions override (optional)
 * @param {Object} input.metadata - Additional metadata (optional)
 * @returns {Promise<Object>} Created client resource map record
 * @throws {Error} If validation fails or GraphQL mutation fails
 */
export const createClientResourceMap = async (input) => {
  // Validate required fields
  if (!input.clientId || typeof input.clientId !== 'string') {
    throw new Error('Client ID is required');
  }
  if (!input.resourceId || typeof input.resourceId !== 'string') {
    throw new Error('Resource ID is required');
  }
  if (typeof input.isEnabled !== 'boolean') {
    throw new Error('isEnabled must be a boolean value');
  }
  if (typeof input.order !== 'number' || input.order < 1 || input.order > 100) {
    throw new Error('Order must be a number between 1 and 100');
  }

  try {
    const result = await API.graphql(
      graphqlOperation(createClientResourceMapMutation, { input })
    );
    logger.info('Client resource map created successfully', { id: result.data.createClientResourceMap.id });
    return result.data.createClientResourceMap;
  } catch (error) {
    logger.error('Failed to create client resource map', { input, error });
    throw new Error('Unable to create client resource map. Please try again.');
  }
};

/**
 * Updates an existing client resource map
 * @param {Object} input - Client resource map update data
 * @param {string} input.id - Client resource map ID (required)
 * @returns {Promise<Object>} Updated client resource map record
 * @throws {Error} If validation fails or GraphQL mutation fails
 */
export const updateClientResourceMap = async (input) => {
  if (!input.id || typeof input.id !== 'string') {
    throw new Error('Client resource map ID is required');
  }

  // Validate order if provided
  if (input.order !== undefined && (typeof input.order !== 'number' || input.order < 1 || input.order > 100)) {
    throw new Error('Order must be a number between 1 and 100');
  }

  try {
    const result = await API.graphql(
      graphqlOperation(updateClientResourceMapMutation, { input })
    );
    logger.info('Client resource map updated successfully', { id: input.id });
    return result.data.updateClientResourceMap;
  } catch (error) {
    logger.error('Failed to update client resource map', { input, error });
    throw new Error('Unable to update client resource map. Please try again.');
  }
};

/**
 * Batch updates client resource map order values
 * @param {Array<{id: string, order: number}>} updates - Array of order updates
 * @returns {Promise<Array>} Array of updated client resource map records
 * @throws {Error} If validation fails or GraphQL mutation fails
 */
export const batchUpdateClientResourceMapOrder = async (updates) => {
  if (!Array.isArray(updates) || updates.length === 0) {
    throw new Error('Updates must be a non-empty array');
  }

  // Validate each update
  updates.forEach((update, index) => {
    if (!update.id || typeof update.id !== 'string') {
      throw new Error(`Update at index ${index} requires a valid id`);
    }
    if (typeof update.order !== 'number' || update.order < 1 || update.order > 100) {
      throw new Error(`Update at index ${index} requires a valid order (1-100)`);
    }
  });

  try {
    const updatePromises = updates.map((update) =>
      API.graphql(
        graphqlOperation(updateClientResourceMapMutation, {
          input: { id: update.id, order: update.order },
        })
      )
    );

    const results = await Promise.all(updatePromises);
    logger.info('Client resource map orders updated successfully', { count: results.length });

    return results.map((result) => result.data.updateClientResourceMap);
  } catch (error) {
    logger.error('Failed to batch update client resource map orders', { error });
    throw new Error('Unable to update resource orders. Please try again.');
  }
};

/**
 * Deletes a client resource map
 * @param {string} id - Client resource map ID
 * @returns {Promise<Object>} Deleted client resource map record
 * @throws {Error} If validation fails or GraphQL mutation fails
 */
export const deleteClientResourceMap = async (id) => {
  if (!id || typeof id !== 'string') {
    throw new Error('Valid id is required');
  }

  try {
    const result = await API.graphql(
      graphqlOperation(deleteClientResourceMapMutation, { input: { id } })
    );
    logger.info('Client resource map deleted successfully', { id });
    return result.data.deleteClientResourceMap;
  } catch (error) {
    logger.error('Failed to delete client resource map', { id, error });
    throw new Error('Unable to delete client resource map. Please try again.');
  }
};

// =============================================================================
// RESOURCE CODE AUTO-GENERATION
// =============================================================================

/**
 * Generates the next available resource code for a given resource type
 * @param {string} resourceTypeId - Resource type ID
 * @returns {Promise<string>} Next available resource code (e.g., "R0089")
 * @throws {Error} If resourceTypeId is invalid, resource type not found, codes exhausted, or query fails
 */
export const getNextResourceCode = async (resourceTypeId) => {
  // Validate input
  if (!resourceTypeId || typeof resourceTypeId !== 'string') {
    throw new Error('Valid resourceTypeId is required');
  }

  try {
    // Fetch resource type to get prefix
    const resourceType = await fetchResourceType(resourceTypeId);

    if (!resourceType) {
      throw new Error('Resource type not found');
    }

    const prefix = resourceType.resourcePrefix.toUpperCase(); // Ensure uppercase

    // Query all resources for this resource type using GSI
    const result = await API.graphql(
      graphqlOperation(resourceCatalogByType, {
        resourceTypeId,
        limit: 1000, // Adjust if needed for large datasets
      })
    );

    const items = result.data.resourceCatalogByType.items || [];

    // Filter to only include resources with matching prefix
    const prefixedItems = items.filter(item =>
      item.resourceCode && item.resourceCode.startsWith(prefix)
    );

    // Extract and parse hex sequences
    const sequences = prefixedItems
      .map(r => {
        // Extract hex part (everything after first character)
        const hexPart = r.resourceCode.substring(1);
        // Parse as hexadecimal integer
        return parseInt(hexPart, 16);
      })
      .filter(n => !isNaN(n)); // Filter out invalid hex sequences

    // Find next available sequence
    const maxSeq = sequences.length > 0 ? Math.max(...sequences) : 0;
    const nextSeq = maxSeq + 1;

    // Check exhaustion (max is 0xFFFF = 65535)
    if (nextSeq > 0xFFFF) {
      logger.error('Resource code exhaustion', {
        prefix,
        nextSeq,
        maxSeq,
        resourceTypeId,
      });
      throw new Error(`All resource codes exhausted for prefix ${prefix}. Maximum 65,536 codes reached.`);
    }

    // Format as 4-character uppercase hex with leading zeros
    const formattedSequence = nextSeq.toString(16).toUpperCase().padStart(4, '0');

    const resourceCode = `${prefix}${formattedSequence}`;

    logger.info('Generated next resource code', {
      resourceTypeId,
      prefix,
      nextSeq,
      resourceCode,
    });

    return resourceCode;
  } catch (error) {
    // Re-throw known errors
    if (error.message.includes('Resource type not found') ||
        error.message.includes('codes exhausted') ||
        error.message.includes('required')) {
      throw error;
    }

    // Log and wrap unexpected errors
    logger.error('Failed to generate next resource code', { resourceTypeId, error });
    throw new Error('Unable to generate resource code. Please try again.');
  }
};

/**
 * Validates a resource code for format and uniqueness
 * @param {string} resourceCode - Resource code to validate
 * @param {string} resourceTypeId - Resource type ID
 * @param {string|null} excludeId - ID to exclude from uniqueness check (for updates)
 * @returns {Promise<Object>} Validation result { valid: boolean, error?: string }
 * @throws {Error} If input is invalid, resource type not found, or query fails
 */
export const validateResourceCode = async (resourceCode, resourceTypeId, excludeId = null) => {
  // Validate inputs
  if (!resourceCode || typeof resourceCode !== 'string') {
    throw new Error('Valid resourceCode is required');
  }

  if (!resourceTypeId || typeof resourceTypeId !== 'string') {
    throw new Error('Valid resourceTypeId is required');
  }

  try {
    // Fetch resource type to get expected prefix
    const resourceType = await fetchResourceType(resourceTypeId);

    if (!resourceType) {
      throw new Error('Resource type not found');
    }

    const expectedPrefix = resourceType.resourcePrefix.toUpperCase(); // Ensure uppercase

    // Validate format using regex: prefix + 4 hex chars (0-9, A-F uppercase)
    const formatRegex = new RegExp(`^${expectedPrefix}[0-9A-F]{4}$`);

    if (!formatRegex.test(resourceCode)) {
      // Check specific error cases for better messaging
      const prefix = resourceCode.charAt(0);

      if (prefix !== expectedPrefix) {
        return {
          valid: false,
          error: `Resource code prefix must be ${expectedPrefix}`,
        };
      }

      return {
        valid: false,
        error: `Resource code must match pattern ${expectedPrefix}#### with hexadecimal characters (0-9, A-F)`,
      };
    }

    // Check uniqueness using GSI
    const existing = await fetchResourceCatalogByCode(resourceCode);

    if (existing) {
      // If we're updating and the existing record is the one being updated, it's valid
      if (excludeId && existing.id === excludeId) {
        logger.info('Resource code validation passed (updating same record)', {
          resourceCode,
          resourceTypeId,
          excludeId,
        });
        return { valid: true };
      }

      // Code already exists on a different resource
      logger.warn('Resource code already exists', {
        resourceCode,
        existingId: existing.id,
        excludeId,
      });

      return {
        valid: false,
        error: `Resource code ${resourceCode} already exists`,
      };
    }

    // All validations passed
    logger.info('Resource code validation passed', {
      resourceCode,
      resourceTypeId,
    });

    return { valid: true };
  } catch (error) {
    // Re-throw known errors
    if (error.message.includes('Resource type not found') ||
        error.message.includes('required')) {
      throw error;
    }

    // Log and wrap unexpected errors
    logger.error('Failed to validate resource code', { resourceCode, resourceTypeId, error });
    throw new Error('Unable to validate resource code. Please try again.');
  }
};

// =============================================================================
// VALIDATION HELPERS
// =============================================================================

/**
 * Validates that a resource code is unique
 * @param {string} resourceCode - Resource code to validate
 * @param {string|null} excludeId - ID to exclude from uniqueness check (for updates)
 * @returns {Promise<boolean>} True if unique, false otherwise
 * @throws {Error} If validation query fails
 */
export const validateResourceCodeUnique = async (resourceCode, excludeId = null) => {
  if (!resourceCode || typeof resourceCode !== 'string') {
    throw new Error('Valid resourceCode is required');
  }

  try {
    const existing = await fetchResourceCatalogByCode(resourceCode);

    if (!existing) {
      return true; // No existing record found, code is unique
    }

    // If we're updating and the existing record is the one being updated, it's valid
    if (excludeId && existing.id === excludeId) {
      return true;
    }

    return false; // Code already exists
  } catch (error) {
    logger.error('Failed to validate resource code uniqueness', { resourceCode, error });
    throw new Error('Unable to validate resource code. Please try again.');
  }
};

/**
 * Validates that action values are valid
 * @param {Array<string>} actions - Array of action values to validate
 * @returns {boolean} True if all actions are valid
 * @throws {Error} If any action is invalid
 */
export const validateActions = (actions) => {
  const validActions = ['VIEW', 'EDIT', 'CREATE', 'DELETE', 'DEACTIVATE', 'EXPORT'];

  if (!Array.isArray(actions)) {
    throw new Error('Actions must be an array');
  }

  const invalidActions = actions.filter((action) => !validActions.includes(action));

  if (invalidActions.length > 0) {
    throw new Error(`Invalid actions: ${invalidActions.join(', ')}`);
  }

  return true;
};

/**
 * Validates that domain value is valid
 * @param {string} domain - Domain value to validate
 * @returns {boolean} True if domain is valid
 * @throws {Error} If domain is invalid
 */
export const validateDomain = (domain) => {
  const validDomains = [
    'OPERATIONS',
    'FINANCE',
    'HUMAN_RESOURCES',
    'INFORMATION_TECHNOLOGY',
    'MARKETING',
    'EXECUTIVE',
  ];

  if (!validDomains.includes(domain)) {
    throw new Error(`Invalid domain: ${domain}`);
  }

  return true;
};
